<template>
  <v-container v-if="photographer">
    <v-row>
      <v-col cols="12">
        <div
          :class="{ 'mt-n12': $vuetify.breakpoint.smAndDown }"
          class="align-center text-center"
        >
          <div class="mb-6">
            <v-avatar size="120">
              <img
                :src="photographer.avatarUrl"
                :alt="`${photographer.fullName} avatar.`"
              />
            </v-avatar>
          </div>
          <div>
            <h1 class="text-h5 font-weight-bold mb-2">
              {{ photographer.fullName }}
            </h1>
            <div class="mb-2">
              <div class="d-flex justify-center">
                <div v-if="photographer.hasAnActiveSubscription && photographer.subscriptionType !== types.subscription.type.basic" class="mr-3">
                  <div
                    :class="
                      $vuetify.theme.dark ? 'black--text' : 'secondary--text'
                    "
                    class="d-inline-block primary pro-badge text-body-1 font-weight-bold"
                  >
                    <small>
                      {{
                        photographer.subscriptionType ===
                        types.subscription.type.pro
                          ? 'PRO'
                          : 'PRO +'
                      }}
                    </small>
                  </div>
                </div>
                <h2 class="text-subtitle-1 text-uppercase font-weight-bold">
                  {{ photographer.role }} -
                  <span
                    v-if="
                      photographer.role === types.user.role.curator ||
                        photographer.role === types.user.role.ambassador
                    "
                  >
                    <img
                      class="asa-icon"
                      src="/images/100asa-round-icon.png"
                      alt="100ASA icon."
                      width="16"
                      height="16"
                    />
                  </span>
                  <span v-else>
                    <v-icon small class="mx-1">
                      fal fa-trophy
                    </v-icon>
                    {{ photographer.reputation ? photographer.reputation.toLocaleString() : 0 }}
                  </span>
                </h2>
              </div>
            </div>
            <div class="mb-3">
              <h3
                v-if="photographer.joiningDate"
                class="text-body-2 primaryLight--text"
              >
                Member since {{ photographer.joiningDate | printdate }}
              </h3>
            </div>
            <div class="d-flex align-center justify-center mb-6">
              <div class="pointer" @click="openUserDialog('following')">
                <strong>{{ photographer.followingCount }}</strong>
                Following
              </div>
              <v-divider vertical class="mx-6" />
              <div class="pointer" @click="openUserDialog('followers')">
                <strong>{{ photographer.followedByCount }}</strong>
                Followers
              </div>
            </div>
            <div>
              <Buttons
                :me="me"
                :photographer="photographer"
                :start-chat="startChat"
                :is-following="isFollowing"
                :loading-follow="loadingFollow"
                :follow-user="followUser"
                :is-short-profile="true"
                color="primary"
              />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <h3 class="text-h6 mt-6 mb-3">
          Published Photos
        </h3>
        <div v-if="initialLoading">
          <div class="text-center my-6">
            <v-icon class="fa-spin">fal fa-spinner</v-icon>
          </div>
        </div>
        <div v-else>
          <div v-if="photos.length > 0">
            <div>
              <div v-for="(photo, index) in photos" :key="index">
                <nuxt-link :to="`/photo/${photo.id}/${photo.slug}`">
                  <v-hover>
                    <v-img
                      slot-scope="{ hover }"
                      :src="$asa.getImageUrl(photo, 'thumb')"
                      :alt="`Photo by ${photo.photographerName}`"
                      class="mb-2"
                    >
                      <template #placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular
                            indeterminate
                            color="tertiary"
                          ></v-progress-circular>
                        </v-row>
                      </template>
                      <v-fade-transition mode="in-out">
                        <div
                          v-if="hover"
                          class="d-flex transition-fast-in-fast-out photo-overlay pa-2"
                          style="height: 50px"
                        >
                          <div
                            class="d-flex pl-1 credits justify-end align-center w-100-percentage"
                          >
                            <div>
                              <v-btn
                                color="secondary"
                                icon
                                @click="openAddToGalleryDialog($event, photo)"
                              >
                                <v-icon small>
                                  fal fa-layer-plus
                                </v-icon>
                              </v-btn>
                              <v-btn
                                :disabled="
                                  likePhotoBusy && likePhotoId === photo.id
                                "
                                :loading="
                                  likePhotoBusy && likePhotoId === photo.id
                                "
                                dark
                                color="secondary"
                                icon
                                @click="
                                  likePhoto(
                                    $event,
                                    photo.hasLikedAlready ? -1 : 1,
                                    photo.id,
                                    photo.photographerId,
                                    'photographers'
                                  )
                                "
                              >
                                <v-icon small>
                                  {{ photo.hasLikedAlready ? 'fas' : 'fal' }}
                                  fa-heart
                                </v-icon>
                              </v-btn>
                            </div>
                          </div>
                        </div>
                      </v-fade-transition>
                    </v-img>
                  </v-hover>
                </nuxt-link>
                <h4 class="text-h6 font-weight-bold">
                  {{ photo.title }}
                </h4>
                <h5 class="text-subtitle-2">
                  Published on the
                  <span class="font-weight-bold">{{
                    $asa.getFriendlyPhotoStatusName(photo.statusName)
                  }}</span>
                  gallery
                  {{ photo.publishDate | timeago }}
                </h5>
                <h5 class="text-caption">
                  Category:
                  <span small class="font-weight-bold text-uppercase" text>
                    {{ photo.categoryName }}
                  </span>
                </h5>
                <v-divider v-if="photos.length > index + 1" class="my-6" />
              </div>
            </div>
            <div v-intersect="onIntersect" />
            <div v-if="loading" class="text-center my-6">
              <v-icon class="fa-spin">fal fa-spinner</v-icon>
            </div>
          </div>
          <div v-else>
            No published photos yet...
          </div>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="userDialog" width="500">
      <UsersDialog
        :me="me"
        :data="followData"
        :load-more="userDialogLoadMore"
        :loading="loading"
        :is-last-page="isLastPage"
        :follow-user="followUser"
        :loading-follow="loadingFollow"
      />
    </v-dialog>
    <Galleries-dialog />
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import Buttons from '@/components/photographer/Buttons.vue'
import types from '@/utils/types'
import { likePhotoMixin } from '@/mixins/likePhotoMixin'
import { EventBus } from '@/plugins/event-bus'

export default {
  name: 'PhotographerShortProfile',
  filters: {
    timeago: date => {
      return moment
        .utc(date)
        .local()
        .fromNow()
    },
    printdate: date => {
      return moment
        .utc(date)
        .local()
        .format('D MMMM YYYY')
    }
  },
  components: {
    Buttons,
    UsersDialog: () => import('@/components/common/UsersDialog.vue'),
    GalleriesDialog: () => import('@/components/galleries/GalleriesDialog.vue')
  },
  mixins: [likePhotoMixin],
  props: {
    photographer: {
      type: Object,
      default: () => {}
    },
    loadData: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      types,
      userDialog: false,
      dataLoaded: false,
      loadingFollow: false,
      loading: false,
      initialLoading: false
    }
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      isFollowing: state => state.following.isFollowing,
      followData: state => state.following.data,
      isLastPage: state => state.following.isLastPage,
      page: state => state.following.page,
      photos: state => state.users.photos,
      photosIsLastPage: state => state.users.isLastPage,
      photosPage: state => state.users.page
    }),
    autoLoadDisabled() {
      return this.loading || this.photos.length === 0
    }
  },
  watch: {
    loadData: {
      async handler(value) {
        if (value) {
          try {
            this.initialLoading = true
            const id = this.photographer.id
            if (this.me) {
              if (this.me.id === id) {
                this.nude = true
              } else {
                this.nude = !this.me.isNudeFilterActive
              }
            }
            const data = {
              user: id,
              page: 1,
              nude: this.nude,
              sort: 'desc'
            }
            if (this.me && this.me.id) {
              await this.$store.dispatch('users/isUserBlocked', id)
              await this.$store.dispatch('following/isUserFollowing', {
                id,
                from: 'photographer'
              })
            }
            await this.$store.dispatch('users/getUserPhotos', data)
            this.dataLoaded = true
          } catch (error) {
            this.$asa.appInsights('Comp: photographers/ShortProfile.vue - method: handler', error)
          } finally {
            this.initialLoading = false
          }
        } else {
          this.$store.dispatch('users/resetPhotos')
          this.dataLoaded = false
        }
      },
      immediate: true
    }
  },
  methods: {
    onIntersect(entries) {
      if (
        entries[0].intersectionRatio > 0 &&
        !this.photosIsLastPage &&
        this.photos.length > 0 &&
        !this.loading
      ) {
        this.loadMore()
      }
    },
    async loadMore() {
      this.loading = true
      try {
        const data = {
          user: this.photographer.id,
          page: this.photosPage + 1,
          nude: this.nude,
          sort: 'desc'
        }
        await this.$store.dispatch('users/getUserPhotos', data)
      } catch (error) {
        this.$asa.appInsights('Comp: photographers/ShortProfile.vue - method: loadMore', error)
      } finally {
        this.loading = false
      }
    },
    startChat(photographer) {
      const data = {
        receiver: photographer
      }
      EventBus.$emit('chat', data)
    },
    async followUser(id, following, from) {
      if (this.me) {
        try {
          this.loadingFollow = true
          await this.$store.dispatch(
            `following/${following ? 'unfollow' : 'follow'}`,
            {
              userId: id
            }
          )
          await this.$store.dispatch('feeds/resetStore')
          await this.$store.dispatch('following/isUserFollowing', {
            id,
            from
          })
          await this.$auth.fetchUser()
        } catch (error) {
          this.$asa.appInsights('Comp: photographers/ShortProfile.vue - method: followUser', error)
        } finally {
          this.loadingFollow = false
        }
      } else {
        EventBus.$emit('snackbar', {
          text: 'You need to log in to follow other photographers',
          color: 'info'
        })
      }
    },
    async openUserDialog(type) {
      try {
        this.loading = true
        await this.$store.dispatch('following/resetData')
        await this.$store.dispatch('following/setType', type)
        this.userDialog = true
        await this.$store.dispatch('following/getData', {
          id: this.photographer.id,
          page: 1
        })
      } catch (error) {
        this.$asa.appInsights('Comp: photographers/ShortProfile.vue - method: openUserDialog', error)
      } finally {
        this.loading = false
      }
    },
    async userDialogLoadMore() {
      if (!this.isLastPage && !this.loading) {
        this.loading = true
        try {
          await this.$store.dispatch('following/getData', {
            id: this.photographer.id,
            page: this.page + 1
          })
        } catch (error) {
          this.$asa.appInsights('Comp: photographers/ShortProfile.vue - method: userDialogLoadMore', error)
        } finally {
          this.loading = false
        }
      }
    },
    openAddToGalleryDialog(event, photo) {
      event.preventDefault()
      this.$store.dispatch('galleries/setSelectedPhotoToAdd', photo)
      this.$store.dispatch('galleries/setAddPhotoToGalleryDialogVisible', true)
    }
  }
}
</script>

<style scoped>
.pro-badge {
  border-radius: 4px;
  display: block;
  padding: 0 12px;
}
.asa-icon {
  vertical-align: text-top;
}
.photo-overlay {
  align-items: center;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.6) 100%
  );
  position: absolute;
  bottom: 0;
  width: 100%;
}
</style>
